<template>
  <Container v-bind="containerProps">
    <input
      ref="input"
      v-model="currentValue"
      type="text"
      :maxlength="maxlength"
      :minlength="minlength"
      :class="inputClass"
      v-bind="attributes"
      :autocomplete="autocomplete"
      :placeholder="placeholder"
      :autofocus="autofocus"
      v-on="handlers"
    >

    <template #leading>
      <slot name="leading" />
    </template>

    <template #trailing>
      <slot name="trailing" />
    </template>
  </Container>
</template>

<script setup lang="ts">
import Container from './Container.vue'

// -- Props (component + input mixin + classes mixin + attributes mixin) --
interface Props {
  modelValue?: string | number | null
  autocomplete?: string | null
  maxlength?: string | number | null
  minlength?: string | number | null
  inputClass?: string
  // input mixin props
  id?: string | number | null
  name?: string | null
  required?: boolean
  readonly?: boolean
  clear?: boolean
  disabled?: boolean
  size?: string | number | null
  pattern?: string | null
  placeholder?: string | number | null
  leading?: string | string[] | null
  trailing?: string | string[] | null
  loading?: boolean
  baseClass?: string | Record<string, any> | string[]
  border?: string | Record<string, any> | string[] | boolean
  rounded?: string | Record<string, any> | string[] | boolean
  shadow?: string | Record<string, any> | string[] | boolean
  highlight?: string | Record<string, any> | string[] | boolean
  defaultStatusClass?: string | Record<string, any> | string[]
  warningStatusClass?: string | Record<string, any> | string[]
  errorStatusClass?: string | Record<string, any> | string[]
  successStatusClass?: string | Record<string, any> | string[]
  disabledClass?: string | Record<string, any> | string[]
  defaultSizeClass?: string | Record<string, any> | string[]
  largeSizeClass?: string | Record<string, any> | string[]
  smallSizeClass?: string | Record<string, any> | string[]
  // classes mixin props
  status?: boolean | string | null
  autofocus?: boolean | null
  tabindex?: string | number | null
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: null,
  autocomplete: null,
  maxlength: null,
  minlength: null,
  inputClass: 'h-full w-full bg-transparent text-sm opacity-90',
  id: null,
  name: null,
  required: false,
  readonly: false,
  clear: false,
  disabled: false,
  size: null,
  pattern: null,
  placeholder: null,
  leading: null,
  trailing: null,
  loading: false,
  baseClass: 'h-9 w-full relative flex items-center',
  border: 'border dark:border-gray-800',
  rounded: 'rounded-md',
  shadow: 'shadow-sm',
  highlight: 'focus-within:ring-2',
  defaultStatusClass: 'bg-white dark:bg-gray-900',
  warningStatusClass: 'border-yellow-200 bg-yellow-50 dark:bg-yellow-400/20',
  errorStatusClass: 'border-red-300 bg-red-100 dark:bg-red-400/20',
  successStatusClass: 'border-green-200 bg-green-100 dark:bg-green-400/20',
  disabledClass: 'bg-gray-50 dark:bg-gray-800 cursor-not-allowed opacity-75 dark:opacity-50',
  defaultSizeClass: 'px-2 h-9',
  largeSizeClass: 'px-4 text-lg h-12',
  smallSizeClass: 'px-2 text-sm h-8',
  status: null,
  autofocus: null,
  tabindex: null,
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: any): void
  (e: 'blur', value: any): void
  (e: 'focus', value: any): void
  (e: 'change', value: any): void
  (e: 'keyup', value: any): void
  (e: 'keydown', value: any): void
}>()

const input = ref<HTMLInputElement>()

// -- data from input mixin --
const valueWhenFocus = ref<any>(null)

// -- computed: currentValue --
const currentValue = computed({
  get () {
    return props.modelValue
  },
  set (value: any) {
    emit('update:modelValue', value)
  },
})

// -- classes mixin computed --
const noStatus = computed(() => props.status === null)
const isError = computed(() => props.status === false || props.status === 'error')
const isSuccess = computed(() => props.status === true || props.status === 'success')
const isWarning = computed(() => props.status === 'warning')

const statusClasses = computed(() => {
  const classes: any[] = []
  if (props.disabled) {
    if (props.disabledClass) classes.push(props.disabledClass)
  }
  if (props.size === null && props.defaultSizeClass) classes.push(props.defaultSizeClass)
  else if (props.size === 'sm' && props.smallSizeClass) classes.push(props.smallSizeClass)
  else if (props.size === 'lg' && props.largeSizeClass) classes.push(props.largeSizeClass)
  if (!props.disabled && noStatus.value && props.defaultStatusClass) classes.push(props.defaultStatusClass)
  if (isError.value && props.errorStatusClass) classes.push(props.errorStatusClass)
  else if (isSuccess.value && props.successStatusClass) classes.push(props.successStatusClass)
  else if (isWarning.value && props.warningStatusClass) classes.push(props.warningStatusClass)
  return classes
})

const currentClass = computed(() => {
  const classes: any[] = [props.baseClass]
  return classes.concat(statusClasses.value)
})

// -- input mixin computed --
const attributes = computed(() => ({
  id: props.id,
  name: props.name,
  required: props.required,
  readonly: props.readonly,
  clear: props.clear,
  disabled: props.disabled,
  size: props.size,
  pattern: props.pattern,
  placeholder: props.placeholder,
}))

const handlers = computed(() => ({
  blur: onBlur,
  focus: onFocus,
  keyup: onKeyUp,
  keydown: onKeyDown,
}))

const containerProps = computed(() => {
  const classes = [...currentClass.value]
  if (props.border) classes.push(props.border as string)
  if (props.rounded) classes.push(props.rounded as string)
  if (props.shadow) classes.push(props.shadow as string)
  if (props.highlight) classes.push(props.highlight as string)
  return {
    class: classes,
    baseClass: props.baseClass,
    leading: props.leading,
    trailing: props.trailing,
    loading: props.loading,
  }
})

// -- input mixin methods --
function onBlur (e: Event) {
  emit('blur', e)
  if (currentValue.value !== valueWhenFocus.value) {
    emit('change', currentValue.value)
  }
}

function onFocus (e: Event) {
  emit('focus', e)
  valueWhenFocus.value = currentValue.value
}

function onKeyUp (e: Event) {
  emit('keyup', e)
}

function onKeyDown (e: Event) {
  emit('keydown', e)
}

function focus () {
  input.value?.focus()
}

defineExpose({ focus, input })
</script>
